import React, { useEffect, useState } from "react";
import { BellRing, Share, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  pushSupported, isIOS, isAndroid, isStandalone,
  subscribePush, unsubscribePush, backendSubscribed, sendTestNotification,
} from "@/lib/push";

// "Alertes d'appels manqués" card on the Mon poste page. iPhone requires the
// installed PWA (shows a guide instead of the toggle); Android/desktop work
// directly in the browser.
export const PushNotificationsCard = () => {
  const supported = pushSupported();
  const iosGuide = isIOS() && !isStandalone();
  const [state, setState] = useState("loading"); // loading | enabled | disabled | denied
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported || iosGuide) return;
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    backendSubscribed()
      .then((on) => setState(on ? "enabled" : "disabled"))
      .catch(() => setState("disabled"));
  }, []); // eslint-disable-line

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush(); // user gesture (toggle tap) — required by iOS
      setState("enabled");
      toast.success("Alertes activées sur cet appareil");
    } catch (e) {
      if (e.message === "permission-refusee" || Notification.permission === "denied") {
        setState("denied");
        toast.error("Notifications refusées", {
          description: "Autorisez les notifications pour ce site dans les réglages du navigateur.",
        });
      } else {
        toast.error(e.message || "Activation impossible");
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setState("disabled");
      toast.success("Alertes désactivées sur cet appareil");
    } catch {
      toast.error("Désactivation impossible");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestNotification();
      if (r.sent > 0) {
        toast.success("Notification de test envoyée");
      } else {
        toast.error("Aucun appareil abonné pour ce compte");
      }
    } catch {
      toast.error("Échec de l'envoi de la notification de test");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="push-notifications-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <BellRing className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-slate-800 text-sm">Notifications</p>
            <p className="text-xs text-slate-400 mt-0.5">
              <span className="font-medium text-slate-600">Alertes d'appels manqués</span> — recevez une notification sur cet appareil dès qu'un appel est manqué ou qu'un message vocal arrive.
            </p>
          </div>
        </div>
        {supported && !iosGuide && state !== "denied" && state !== "loading" && (
          <Switch
            data-testid="push-toggle"
            checked={state === "enabled"}
            disabled={busy}
            onCheckedChange={(on) => (on ? enable() : disable())}
            aria-label="Alertes d'appels manqués"
          />
        )}
      </div>

      {iosGuide && (
        <div data-testid="push-ios-guide" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900 mb-2">Sur iPhone, installez d'abord l'application :</p>
          <ol className="text-sm text-amber-900/80 space-y-1.5 list-decimal list-inside">
            <li>Dans Safari, touchez <span className="inline-flex items-center gap-1 font-medium"><Share className="h-3.5 w-3.5" /> Partager</span>.</li>
            <li>Choisissez <span className="font-medium">« Sur l'écran d'accueil »</span>.</li>
            <li>Ouvrez l'app depuis l'icône <span className="font-medium">Standard Call</span>, puis revenez ici activer les alertes.</li>
          </ol>
        </div>
      )}

      {!supported && (
        <p data-testid="push-unsupported" className="mt-3 text-xs text-slate-400">
          Les notifications push ne sont pas prises en charge par ce navigateur.
        </p>
      )}

      {state === "denied" && (
        <p data-testid="push-denied" className="mt-3 text-xs text-red-600">
          Notifications bloquées par le navigateur — autorisez-les dans les réglages du site pour activer les alertes.
        </p>
      )}

      {state === "enabled" && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="push-test-button"
            onClick={sendTest}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50 transition-colors"
          >
            <Send className="h-4 w-4" /> Envoyer une notification de test
          </button>
        </div>
      )}

      {isAndroid() && !isStandalone() && state !== "loading" && (
        <p data-testid="push-android-hint" className="mt-3 text-xs text-slate-400">
          Astuce : vous pouvez aussi installer l'application depuis le menu Chrome (« Installer l'application ») — facultatif, les alertes fonctionnent déjà dans le navigateur.
        </p>
      )}
    </div>
  );
};
